import React from "react";
import { Fragment } from "react";

import clsx from "clsx";
import { FieldValues, Path, RegisterOptions, UseFormRegister, UseFormSetValue, UseFormWatch } from "react-hook-form";

import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, SelectorIcon } from "@heroicons/react/outline";

import classNames from './../../../utils/classNames';

export interface SelectOption {
  value: string; label: string
}

interface StyledSelectInputProps<T extends FieldValues> extends Omit<React.HTMLProps<HTMLInputElement>, 'onChange'> {
  register: UseFormRegister<T>;
  registerName: Path<T>;
  setValue: UseFormSetValue<T>;
  watch: UseFormWatch<T>;
  options: SelectOption[];
  label?: string;
  registerOptions?: RegisterOptions<T, Path<T>>;
  classes?: string;
  labelClasses?: string; // Tailwind CSS margin classes
  listboxClasses?: string;
  onChange?: (value: string) => void;
  description?: string; // Subtext under the label for longer descriptions
}


export function StyledSelectInput<T extends FieldValues>({
  register,
  registerName,
  setValue,
  watch,
  options,
  label,
  registerOptions,
  classes,
  labelClasses,
  listboxClasses,
  description,
  ...rest
}: StyledSelectInputProps<T>) {

  const selectValue = watch(registerName);

  return (
    <div className={classes}>
      <Listbox
        {...register(registerName, registerOptions)}
        value={selectValue}
        onChange={(e) => { setValue(registerName, e, { shouldDirty: true }); rest.onChange && rest.onChange(e); }}
        aria-describedby={description ? `${registerName as string}-desc` : undefined}
      >
        {({ open }) => (
          <>
            {label && (
              <label htmlFor={registerName as string} className={clsx("text-sm font-medium text-gray-700", labelClasses)}>
                {label}
              </label>
            )}
            <div className={clsx("w-full relative", listboxClasses)}>
              <Listbox.Button
                className={clsx("bg-white relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-etpink-500 focus:border-etpink-500 sm:text-sm", label && "mt-1")}>
                <span className="block truncate">
                  {
                    selectValue !== undefined ?
                      options.find(
                        (e) => e.value === selectValue
                      )?.label
                      :
                      label
                  }
                </span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <SelectorIcon
                    className="h-5 w-5 text-gray-700"
                    aria-hidden="true"
                  />
                </span>
              </Listbox.Button>

              <Transition
                show={open}
                appear={true}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options
                  className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                  {options.map((e) => (
                    <Listbox.Option
                      key={e.value}
                      className={({ active }) =>
                        classNames(
                          active
                            ? "text-white bg-etpink-600"
                            : "text-gray-900",
                          "cursor-default select-none relative py-2 pl-8 pr-4"
                        )
                      }
                      value={e.value}
                    >
                      <>
                        <span
                          className={classNames(
                            selectValue === e.value
                              ? "font-semibold"
                              : "font-normal",
                            "block truncate"
                          )}
                        >
                          {e.label}
                        </span>

                        {selectValue === e.value ? (
                          <span className="text-etpink-600 absolute inset-y-0 left-0 flex items-center pl-1.5">
                            <CheckIcon
                              className="h-5 w-5"
                              aria-hidden="true"
                            />
                          </span>
                        ) : null}
                      </>
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
            {description && (
              <p id={`${registerName as string}-desc`} className="text-xs text-gray-500 mt-1">{description}</p>
            )}
          </>
        )}
      </Listbox>
    </div>
  )
}